'use client'

import { useTranslations } from 'next-intl'

import { useAthleteViewerFlags } from '@/features/clubs/hooks/useAthleteProfile'
import { useUserMe } from '@/hooks/useUserMe'
import { rememberFanReturnPath } from '@/lib/auth/fanReturnPath'
import { useAuth } from '@/providers/AuthProvider'
import Button from '@/shared/ui/Button'
import { useAuthStore } from '@/stores/authStore'
import { HeartIcon } from '@/ui/icons/HeartIcon'

import BecomeFanButton from '../BecomeFanButton'
import StopBeingFanButton from '../StopBeingFanButton'
import S from './AthleteFanAction.module.scss'

interface AthleteFanActionProps {
  clubId: string
  /** Display Name, for the switch dialog. */
  name: string
}

/**
 * «Become a fan» on an athlete profile (US-ATH-13.1): a guest is offered
 * to sign in; a fan sees «Become a fan» or «Stop being a fan»; the athlete itself,
 * a club, another athlete, an agent or an admin see nothing. Choosing and
 * switching the favourite: BecomeFanButton (BAT-1107).
 */
const AthleteFanAction = ({ clubId, name }: AthleteFanActionProps) => {
  const t = useTranslations('club.becomeFan')
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)
  const { openAuthModal } = useAuth()
  const { data: me } = useUserMe()
  const viewerId = isAuthenticated ? me?.user?.id : undefined
  const { data: flags } = useAthleteViewerFlags(clubId, viewerId)

  if (!isAuthenticated) {
    return (
      <Button
        className={S.trigger}
        color="secondary"
        type="button"
        onClick={() => {
          // US-ATH-14: back to this profile after signing in or up.
          rememberFanReturnPath(window.location.pathname)
          openAuthModal('login')
        }}
      >
        {t('button')}
        <HeartIcon className={S.heartIcon} />
      </Button>
    )
  }

  // Until the flags arrive nothing is shown: no flash of a wrong state.
  if (!flags) {
    return null
  }

  // BAT-1180: the same «Stop being a fan» as on a club — the switch rules
  // (30 days after a referral sign-up, tasks in work) and their dialog.
  if (flags.isFavorite) {
    return <StopBeingFanButton clubId={clubId} isAthlete />
  }

  if (flags.canBecomeFan) {
    return <BecomeFanButton clubId={clubId} name={name} isAthlete />
  }

  return null
}

export default AthleteFanAction
